import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { type AvailableActions, type CardId, type LocationId, type PlayerView } from '@bcm/rules';
import { BoardScene, type Highlight } from '../scene/BoardScene';
import type { CardPick, CardState } from '../scene/TableCards';
import { director } from '../director/director';
import { useGame } from '../state/store';
import { C, SEAL_COLORS, action, errorText, locationName, pack, values } from '../game/content';
import { audio } from '../audio/sfx';
import { Card, CardBack, CardPreview } from './Card';
import { DecisionBar, DraftModal } from './Decisions';
import { FxLayer, Results, Toasts } from './Overlays';
import { CastPanel, HandTray, MyPanel, RoomPopup, Standings, TopBar } from './Hud';
import { RoomLabels } from './RoomLabels';
import { Coach } from './Coach';
import { hint, pointer } from './Tooltip';

export function Game() {
  const presented = useGame((s) => s.presented);
  const actions = useGame((s) => s.presentedActions);
  const busy = useGame((s) => s.busy);
  const inFlight = useGame((s) => s.inFlight);
  const reducedMotion = useGame((s) => s.settings.reducedMotion);
  const mount = useRef<HTMLDivElement>(null);
  const [scene, setScene] = useState<BoardScene | null>(null);
  const [sceneReady, setSceneReady] = useState(false);
  const [hover, setHover] = useState<{ loc: LocationId; x: number; y: number } | null>(null);
  const [hoverCard, setHoverCard] = useState<{ pick: CardPick; x: number; y: number } | null>(null);
  const [discardSel, setDiscardSel] = useState<CardId[]>([]);

  useEffect(() => {
    let gfx = new URLSearchParams(location.search).get('gfx');
    try {
      gfx ??= localStorage.getItem('bcm.gfx');
    } catch {
      /* storage unavailable */
    }
    const sc = new BoardScene(mount.current!, gfx === 'low' || gfx === 'medium' ? gfx : 'high');
    sc.onRoomClick = onRoomClick;
    sc.onRoomHover = (loc, x, y) => setHover(loc ? { loc, x, y } : null);
    sc.onCardHover = (pick, x, y) => setHoverCard(pick ? { pick, x, y } : null);
    sc.onCardClick = onCardClick;
    if (import.meta.env.DEV) (globalThis as unknown as { __bcmScene: BoardScene }).__bcmScene = sc;
    director.bindScene(sc);
    setScene(sc);
    void sc.ready.then(() => setSceneReady(true));
    audio.ambienceOn(true);
    return () => {
      audio.ambienceOn(false);
      director.bindScene(null);
      sc.dispose();
    };
  }, []);

  const decision = actions?.decisions[0];
  useEffect(() => {
    if (!scene || !presented) return;
    scene.reducedMotion = reducedMotion;
    scene.cards.reducedMotion = reducedMotion;
    scene.setHighlights(highlightsFor(actions, busy || inFlight));
    const { spells, problems } = cardStatesFor(presented, actions, busy || inFlight);
    scene.cards.setStates(spells, problems);
  }, [scene, presented, actions, busy, inFlight, reducedMotion]);

  // Market decisions: ease the camera onto the cards being chosen from, and back afterwards.
  const focus = decision?.kind === 'buySpell' ? 'spells' : decision?.kind === 'claimProblem' ? 'problems' : null;
  useEffect(() => {
    scene?.focusCards(focus);
  }, [scene, focus]);

  // A fresh discard decision starts with nothing selected.
  const decisionId = decision?.id;
  useEffect(() => setDiscardSel([]), [decisionId]);
  const toggleDiscard = (c: CardId) => {
    if (decision?.kind !== 'discardActions' || !decision.cards.includes(c)) return;
    audio.play('select');
    setDiscardSel((sel) => (sel.includes(c) ? sel.filter((x) => x !== c) : sel.length < decision.count ? [...sel, c] : [...sel.slice(1), c]));
  };

  useKeyboard();
  useWakeLock(useGame((s) => s.settings.keepAwake) && presented?.phase !== 'ended');
  useSafeArea(scene, !!presented);

  // The board element must keep a stable position in the tree: the Three.js canvas lives inside it.
  return (
    <div className="game">
      <div className="board" ref={mount}>
        {(!presented || !sceneReady) && (
          <div className="loading">
            <span className="loading-crest" />
            Opening the Ministry…
          </div>
        )}
        {presented && scene && sceneReady && !focus && <RoomLabels scene={scene} view={presented} onClick={onRoomClick} />}
      </div>
      {presented && (
        <>
          <TopBar view={presented} actions={actions} onResetCamera={() => scene?.resetCamera()} />
          <MyPanel view={presented} actions={actions} />
          <Standings view={presented} />
          <HandTray view={presented} actions={actions} discardSel={discardSel} onDiscardToggle={toggleDiscard} />
          {hover && !focus && <RoomPopup loc={hover.loc} x={hover.x} y={hover.y} view={presented} actions={actions} />}
          {hoverCard && <CardInspect hover={hoverCard} view={presented} actions={actions} />}
          <CastPanel view={presented} actions={actions} />
          <DecisionBar view={presented} actions={actions} discardSel={discardSel} clearDiscard={() => setDiscardSel([])} />
          <DraftModal view={presented} actions={actions} />
          <Coach view={presented} actions={actions} />
          <Results view={presented} />
          <FxLayer view={presented} />
        </>
      )}
      <CardPreview />
      <Toasts />
    </div>
  );
}

/** Tell the scene which parts of the screen the floating HUD covers, so framing keeps the table clear. */
function useSafeArea(scene: BoardScene | null, ready: boolean): void {
  useEffect(() => {
    if (!scene || !ready) return;
    const measure = () => {
      const r = (sel: string) => document.querySelector(sel)?.getBoundingClientRect();
      // The rivals bar sits under the top bar; its drop-down lists float over the board.
      const top = Math.max(r('.topbar')?.bottom ?? 0, r('.rivals-bar')?.bottom ?? 0);
      const left = r('.my-panel')?.right ?? 0;
      const score = r('.score-col');
      const right = score ? innerWidth - score.left : 0;
      const tray = r('.tray');
      const bottom = tray ? innerHeight - tray.top : 0;
      scene.setSafeArea({ left, right, top, bottom });
    };
    measure();
    const ro = new ResizeObserver(measure);
    for (const sel of ['.topbar', '.rivals-bar', '.my-panel', '.score-col', '.tray']) {
      const el = document.querySelector(sel);
      if (el) ro.observe(el);
    }
    addEventListener('resize', measure);
    return () => {
      ro.disconnect();
      removeEventListener('resize', measure);
    };
  }, [scene, ready]);
}

function highlightsFor(actions: AvailableActions | null, locked: boolean): Partial<Record<LocationId, Highlight>> {
  const out: Partial<Record<LocationId, Highlight>> = {};
  if (!actions || locked) return out;
  const d = actions.decisions[0];
  if (d?.kind === 'chooseLocation') {
    for (const l of d.locations) out[l] = d.purpose === 'audit' ? 'audit' : 'target';
    return out;
  }
  if (d) return out;
  if (actions.free && actions.canPlace) for (const l of actions.locations) if (l.legal) out[l.location] = 'legal';
  return out;
}

/** During a market decision the options light up and everything else on that side dims. */
function cardStatesFor(view: PlayerView, actions: AvailableActions | null, locked: boolean): { spells: Map<CardId, CardState>; problems: Map<string, CardState> } {
  const spells = new Map<CardId, CardState>();
  const problems = new Map<string, CardState>();
  const d = actions?.decisions[0];
  if (!d || locked) return { spells, problems };
  if (d.kind === 'buySpell') {
    for (const c of view.arcanium) if (c) spells.set(c, 'dim');
    for (const o of d.options) spells.set(o.card, 'playable');
  }
  if (d.kind === 'claimProblem') {
    for (const p of view.problems) problems.set(p.problem, 'dim');
    for (const o of d.options) problems.set(o.problem, 'playable');
  }
  return { spells, problems };
}

function onCardClick(pick: CardPick): void {
  const s = useGame.getState();
  if (s.busy || s.inFlight) return;
  const d = s.presentedActions?.decisions[0];
  const say = (text: string, kind: 'info' | 'warn' = 'info') => {
    if (kind === 'warn') audio.play('error');
    hint(pointer.x, pointer.y, text, kind);
  };
  if (pick.kind === 'spell') {
    const opt = d?.kind === 'buySpell' ? d.options.find((o) => o.card === pick.card) : undefined;
    if (opt && d) return s.command({ t: 'decide', decision: d.id, choice: { card: pick.card } });
    if (d?.kind === 'buySpell') {
      const def = s.presented?.cards[pick.card];
      const price = def ? values(def, s.presented).price : 0;
      const mana = s.presented?.seats[s.you?.seat ?? 0]?.mana ?? 0;
      return say(`Costs ${price} mana — you have ${mana}`, 'warn');
    }
    return say('Visit the Arcanium to buy spells');
  }
  if (pick.kind === 'problem') {
    const opt = d?.kind === 'claimProblem' ? d.options.find((o) => o.problem === pick.problem) : undefined;
    if (opt && d) return s.command({ t: 'decide', decision: d.id, choice: { problem: pick.problem } });
    if (d?.kind === 'claimProblem') return say('You don’t meet this requirement yet', 'warn');
    return say('Claim City Problems at the Grand Auditor’s Office');
  }
  if (pick.kind === 'spellDeck') return say(`${s.presented?.spellDeckCount ?? 0} spells left in the deck`);
  if (pick.kind === 'problemDeck') return say('Unrevealed City Problems');
  if (pick.kind === 'actionDeck') return say(`${s.presented?.actionDeckCount ?? 0} Action cards left — draw them at the Archive`);
  if (pick.kind === 'actionDiscard') return say(`${s.presented?.actionDiscard.length ?? 0} played Action cards`);
  if (pick.kind === 'decree') return say('A Decree in force: it applies to every player');
  if (pick.kind === 'decreeDeck') return say(`${s.presented?.decreeDeckCount ?? 0} Decrees in the deck`);
  if (pick.kind === 'decreeDiscard') return say(`${s.presented?.decreeDiscard?.length ?? 0} struck or superseded Decrees`);
}

function CardInspect({ hover, view, actions }: { hover: { pick: CardPick; x: number; y: number }; view: PlayerView; actions: AvailableActions | null }) {
  const { pick } = hover;
  const d = actions?.decisions[0];
  let body: React.ReactNode;
  if (pick.kind === 'spellDeck' || pick.kind === 'problemDeck') {
    body =
      pick.kind === 'spellDeck' ? (
        <div className="inspect-note">
          <b>Spell deck</b>
          <span>{view.spellDeckCount} cards. The Arcanium is refilled from here.</span>
        </div>
      ) : (
        <div className="inspect-note">
          <b>Incident file</b>
          <span>{pack.problems.length - view.problems.length} unrevealed City Problems.</span>
        </div>
      );
  } else if (pick.kind === 'actionDeck') {
    body = (
      <div className="inspect-note">
        <b>Action deck</b>
        <span>{view.actionDeckCount} cards. Draw them at the Archive.</span>
      </div>
    );
  } else if (pick.kind === 'actionDiscard') {
    const top = view.actionDiscard[view.actionDiscard.length - 1];
    const def = top ? view.cards[top] : null;
    body = (
      <>
        {def && <Card def={def} size="lg" preview={false} />}
        <div className="inspect-note">
          <b>Discard pile</b>
          <span>{view.actionDiscard.length} played Action card{view.actionDiscard.length === 1 ? '' : 's'}. Shuffled back in when the deck runs out.</span>
        </div>
      </>
    );
  } else if (pick.kind === 'spell') {
    const def = view.cards[pick.card];
    if (!def) return null;
    const opt = d?.kind === 'buySpell' ? d.options.find((o) => o.card === pick.card) : undefined;
    body = (
      <>
        <Card def={def} view={view} size="lg" preview={false} state={opt ? 'playable' : null} />
        {opt && (
          <div className="inspect-note">
            <b>{pointer.touch ? 'Tap again' : 'Click'} to buy · {opt.price} mana</b>
          </div>
        )}
      </>
    );
  } else if (pick.kind === 'decree') {
    const d0 = view.decrees?.find((x) => x.decree === pick.decree);
    body = (
      <>
        <Card def={pick.decree} size="lg" preview={false} />
        {d0 && (
          <div className="inspect-note">
            {d0.by === null ? (
              <span>Issued by the Ministry · no author</span>
            ) : (
              <span>
                <span className="claim-dot" style={{ background: SEAL_COLORS[d0.by] }} /> Enacted by {view.seats[d0.by]?.name} · 2 VP to them at the end
              </span>
            )}
          </div>
        )}
      </>
    );
  } else if (pick.kind === 'decreeDeck' || pick.kind === 'decreeDiscard') {
    const top = pick.kind === 'decreeDiscard' ? view.decreeDiscard?.[view.decreeDiscard.length - 1] : undefined;
    body = (
      <>
        {top && <Card def={top} size="lg" preview={false} />}
        <div className="inspect-note">
          {pick.kind === 'decreeDeck' ? (
            <>
              <b>Decree deck</b>
              <span>
                {view.decreeDeckCount ?? 0} Decrees. {view.options.decreeRounds?.length ? `The Ministry issues one from here at the start of round${view.options.decreeRounds.length > 1 ? 's' : ''} ${view.options.decreeRounds.join(' and ')}.` : ''}
              </span>
            </>
          ) : (
            <>
              <b>Struck Decrees</b>
              <span>{view.decreeDiscard?.length ?? 0} repealed or superseded. The deck is refilled from here when it runs out.</span>
            </>
          )}
        </div>
      </>
    );
  } else {
    const slot = view.problems.find((p) => p.problem === pick.problem);
    const opt = d?.kind === 'claimProblem' ? d.options.find((o) => o.problem === pick.problem) : undefined;
    // P-17: where this Problem's Resolution stands.
    const res = slot?.resolution ? C.resolutions.get(slot.resolution) : undefined;
    const queued = res && view.resolutions?.queued.some((r) => r.problem === pick.problem);
    const enacted = res && view.resolutions?.inForce.find((r) => r.problem === pick.problem);
    const inMana = Object.values(opt?.manaPermits ?? {}).reduce((n, x) => n + (x ?? 0), 0) * 3;
    body = (
      <>
        <Card def={pick.problem} size="lg" preview={false} state={opt ? 'playable' : null} />
        {(opt || !!slot?.claims.length || res) && (
          <div className="inspect-note">
            {opt && (
              <b>
                {pointer.touch ? 'Tap again' : 'Click'} to claim · {opt.vp} VP{inMana ? ` · ${inMana} mana for the missing permits` : ''}
              </b>
            )}
            {res && (
              <div className="inspect-res">
                <span className="res-dot" />
                {enacted ? (
                  <span>
                    <b>{res.name}</b> is in force for everyone.
                  </span>
                ) : queued ? (
                  <span>
                    <b>{res.name}</b> takes effect for everyone next round.
                  </span>
                ) : (
                  <span>
                    Claim it first to pass <b>{res.name}</b> — for every player, from the next round.
                  </span>
                )}
              </div>
            )}
            {slot?.claims.map((seat, i) => (
              <div key={seat}>
                <span className="claim-dot" style={{ background: SEAL_COLORS[seat] }}>
                  {i === 0 ? 'I' : 'II'}
                </span>{' '}
                Claimed by {view.seats[seat]?.name}
              </div>
            ))}
          </div>
        )}
      </>
    );
  }
  return <InspectBox x={hover.x} y={hover.y} width={pick.kind === 'problem' ? 350 : pick.kind === 'spellDeck' || pick.kind === 'problemDeck' || pick.kind === 'actionDeck' || pick.kind === 'decreeDeck' ? 230 : 250}>{body}</InspectBox>;
}

/** Sits beside the pointer, vertically centred on it, whatever its height; kept on screen. */
function InspectBox({ x, y, width, children }: { x: number; y: number; width: number; children: React.ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  const [top, setTop] = useState(y);
  useLayoutEffect(() => {
    const h = ref.current?.offsetHeight ?? 0;
    setTop(Math.max(66, Math.min(innerHeight - h - 12, y - h / 2)));
  }, [x, y, children]);
  const left = x > innerWidth / 2 ? x - width - 22 : x + 22;
  return (
    <div ref={ref} className="card-inspect" style={{ left, top, width }}>
      {children}
    </div>
  );
}

function onRoomClick(loc: LocationId): void {
  const s = useGame.getState();
  const a = s.presentedActions;
  const say = (text: string) => {
    audio.play('error');
    hint(pointer.x, pointer.y, text);
  };
  if (s.busy || s.inFlight) return;
  if (!a) return hint(pointer.x, pointer.y, s.you?.seat == null ? 'You are spectating' : 'Wait for your turn', 'info');
  const d = a.decisions[0];
  if (d?.kind === 'chooseLocation') {
    if (d.locations.includes(loc)) s.command({ t: 'decide', decision: d.id, choice: { location: loc } });
    else say('Choose one of the lit rooms');
    return;
  }
  if (d) return say('Finish your current choice first');
  if (!a.free) return hint(pointer.x, pointer.y, 'Wait for your turn', 'info');
  const opt = a.locations.find((l) => l.location === loc);
  if (!opt?.legal) return say(errorText(opt?.error ?? a.placeError));
  s.command({ t: 'place', location: loc });
}

/**
 * Keep the screen on during a game (phones dim and lock after a short idle time, and a turn spent
 * thinking or watching the rivals looks idle). The browser releases the lock whenever the page is
 * hidden, so it is taken again each time the game comes back into view.
 */
function useWakeLock(on: boolean): void {
  useEffect(() => {
    const wl = (navigator as Navigator & { wakeLock?: { request(type: 'screen'): Promise<{ release(): Promise<void> }> } }).wakeLock;
    if (!on || !wl) return;
    let lock: { release(): Promise<void> } | null = null;
    let alive = true;
    const take = () => {
      if (document.visibilityState !== 'visible' || lock) return;
      wl.request('screen')
        .then((l) => {
          if (!alive) return void l.release();
          lock = l;
          (l as unknown as EventTarget).addEventListener?.('release', () => (lock = null));
        })
        .catch(() => undefined); // e.g. battery saver: carry on without it
    };
    take();
    document.addEventListener('visibilitychange', take);
    return () => {
      alive = false;
      document.removeEventListener('visibilitychange', take);
      void lock?.release();
      lock = null;
    };
  }, [on]);
}

function useKeyboard(): void {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.target as HTMLElement)?.closest?.('input, textarea, select')) return;
      const s = useGame.getState();
      const a = s.presentedActions;
      if (e.key === ' ' && a?.free && a.canEndTurn && !s.busy) {
        e.preventDefault();
        s.command({ t: 'endTurn' });
      } else if ((e.key === 'z' || e.key === 'Z') && a?.canUndo) {
        s.undo();
      } else if (e.key === 'Escape') {
        s.select(null);
      } else if (e.key === 'm' || e.key === 'M') {
        const v = s.settings.volume;
        s.setSettings({ volume: v > 0 ? 0 : 0.8 });
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
}

export { locationName, action };
export { CardBack };
